import { Box } from '@mui/material';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Edge, useEdges } from 'reactflow';
import '../../assets/styles/animatedBorder.scss';
import { FlowService } from '../../service/FlowService';
import { ScriptingService } from '../../service/ScriptingService';
import { useAuthStore } from '../../store/storeAuth';
import { useNotificationStore } from '../../store/storeNotifications';
import useFlowsStore from '../../store/storeFlows';
import { NodeProps } from '../../util/Types/NodeProps';
import { nodeDataAutoSaveDynamic } from '../../util/autosave/nodedata_autosave';
import { checkExecuteTrigger } from '../../util/checkExecute';
import { useDebounceEffect } from '../../util/useDebounceEffect';
import ScriptEditor from '../Scripting/ScriptingHelper';
import Node from '../UI/Node/Node';
import { nodeColorBasedOnType } from '../UI/Node/nodeConstants';
import OutputTextarea from '../UI/OutputTextarea/OutputTextarea';

const Scripting = ({ id, data, type }: NodeProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const edges: Edge<any>[] = useEdges();
  const [btnLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const setNotification = useNotificationStore(state => state.setNotification);

  const setNodeState = useFlowsStore(state => state.setNodeState);
  const setSchema = useFlowsStore(state => state.setSchema);
  const setSaving = useFlowsStore(state => state.setSaving);
  const flowId = useFlowsStore(state => state.flowId);

  const [variables, setVariables] = useState<
    {
      id: string;
      name: string;
      value: string;
    }[]
  >([]);

  const [hasToExecute, setHasToExecute] = useState(true);
  const [executeCounter, setExecuteCounter] = useState(0);
  const [script, setScript] = useState('');
  const [language, setLanguage] = useState('JavaScript');
  const [isChangingDirectly, setIsChangingDirectly] = useState(false);
  const [whoIsChanging, setWhoIsChanging] = useState<{
    value: any;
    name: string;
  }>({
    value: '',
    name: '',
  });
  const prevScriptDataRef = useRef<any>(null);

  useEffect(() => {
    setIsInitialized(true);
  }, []);

  useEffect(() => {
    if (!isChangingDirectly) {
      if (data?.variables && data.variables.length > 0) {
        setVariables(data.variables);
      } else if (!data?.variables) {
        const defaultVariable = {
          id: 'func' + Math.random().toString(36).substr(2, 9),
          name: 'varOne',
          value: '',
        };
        setVariables([defaultVariable]);
        nodeDataAutoSaveDynamic({
          newEdges: edges,
          setNodeState,
          id,
          setSaving,
          flowId,
          objectData: { variables: [defaultVariable] },
          setSchema,
        });
      }
    }
  }, [data?.variables]);

  useEffect(() => {
    if (!isChangingDirectly) {
      setLanguage(data?.language || 'JavaScript');
      if (!data?.language) {
        nodeDataAutoSaveDynamic({
          newEdges: edges,
          setNodeState,
          id,
          setSaving,
          flowId,
          objectData: { language: 'JavaScript' },
          setSchema,
        });
      }
    }
  }, [data?.language]);

  useEffect(() => {
    if (!isChangingDirectly && (!data?.scriptData || data.scriptData.length === 0)) {
      setScript(data?.script || '');
    }
  }, [data?.script]);

  useEffect(() => {
    if (isInitialized && data?.scriptData) {
      const currentScriptData = JSON.stringify(data.scriptData);
      const prevScriptData = JSON.stringify(prevScriptDataRef.current);

      if (currentScriptData !== prevScriptData) {
        prevScriptDataRef.current = data.scriptData;

        if (data.scriptData.length > 0) {
          const scriptText = data.scriptData
            .map((item: any) => item.text)
            .join(' ');

          setScript(scriptText);

          const persistedScript =
            typeof data.script === 'string' ? data.script : '';
          if (scriptText !== persistedScript) {
            nodeDataAutoSaveDynamic({
              newEdges: edges,
              setNodeState,
              id,
              setSaving,
              flowId,
              objectData: {
                scriptData: data.scriptData,
                script: scriptText,
              },
              setSchema,
            });
          }
        } else {
          setScript('');
        }
      }
    } else if (
      isInitialized &&
      !data?.scriptData &&
      prevScriptDataRef.current
    ) {
      prevScriptDataRef.current = null;
      setScript('');
    }
  }, [data?.scriptData, isInitialized]);

  const addVariable = () => {
    const newVariable = {
      id: 'func' + Math.random().toString(36).substr(2, 9),
      name: '',
      value: '',
    };

    const updatedVariables = [...variables, newVariable];
    setVariables(updatedVariables);
    setWhoIsChanging({ name: 'variables', value: updatedVariables });
    setIsChangingDirectly(true);
  };

  const debounceTimeoutRef = useRef<any>();
  useEffect(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (data?.flowTrigger?.id && !data?.canceled && !data?.paused) {
      if (hasToExecute) {
        debounceTimeoutRef.current = setTimeout(() => {
          setHasToExecute(false);
          execute();
        }, 1000);
      }
    }

    return () => {
      clearTimeout(debounceTimeoutRef.current);
    };
  }, [data?.flowTrigger, hasToExecute, variables, script, data?.canceled]);

  const execute = async () => {
    let prefixLineCount = 0;
    try {
      setIsLoading(true);

      let scriptData = script;

      if (data?.scriptData) {
        scriptData = data?.scriptData?.map((item: any) => item.text).join(' ');
      }

      const formattedVariables = variables?.map((item: any) => {
        let value;

        const isNumber = !isNaN(item?.value);
        if (isNumber) {
          value = Number(item.value);
        } else if (item.value === 'true') {
          value = true;
        } else if (item.value === 'false') {
          value = false;
        } else {
          value = item.value;
        }

        return { name: item.name, value };
      });

      const stripCodeFence = (input: string): string => {
        const fenced = input.match(
          /^\s*```[a-zA-Z0-9_+-]*[ \t]*\r?\n?([\s\S]*?)\r?\n?[ \t]*```\s*$/,
        );
        return fenced ? fenced[1] : input;
      };

      const variablesString = formattedVariables
        ?.map((variable: any) => {
          if (language === 'JavaScript') {
            return `const ${variable.name} = ${JSON.stringify(
              variable.value,
            )};`;
          } else if (language === 'Python') {
            const pythonValue =
              typeof variable.value === 'string'
                ? `"""${stripCodeFence(variable.value)}"""`
                : variable.value;
            return `${variable.name} = ${pythonValue}`;
          }
          return '';
        })
        .join('\n');

      prefixLineCount = variablesString
        ? variablesString.split('\n').length + 1
        : 0;

      const scriptWithVariables = `${variablesString}\n\n${scriptData}`.trim();

      const body = {
        script: scriptWithVariables,
        endpoint:
          language === 'JavaScript' ? '/script/javascript' : '/script/python',
      };

      const response = await ScriptingService.scripting(body);

      const executedResult = response.data.result.result;
      const executedConsole = response.data.result.consoleOutput;
      const typeOfObject = typeof executedResult === 'object';

      nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState,
        id,
        setSaving,
        flowTriggerData: checkExecuteTrigger(data, id),
        changeType: 'execute',
        flowId,
        objectData: {
          result: typeOfObject
            ? JSON.stringify(executedResult)
            : executedResult,
          console: executedConsole || ' ',
        },
        setSchema,
      });
      setExecuteCounter(0);

      setTimeout(() => {
        setIsLoading(false);
        setHasToExecute(true);
      }, 1000);
    } catch (error: any) {
      const rawMessage = error?.message || 'Error running script';
      const consoleMessage =
        prefixLineCount > 0
          ? rawMessage.replace(
              /ScriptNode,\s*line\s+(\d+)/g,
              (_match: string, lineStr: string) => {
                const adjusted = parseInt(lineStr, 10) - prefixLineCount;
                return `ScriptNode, line ${adjusted > 0 ? adjusted : 1}`;
              },
            )
          : rawMessage;
      nodeDataAutoSaveDynamic({
        newEdges: edges,
        setNodeState,
        setSaving,
        id,
        flowTriggerData: checkExecuteTrigger(data, id),
        objectData: {
          result: 'Error executing script',
          console: consoleMessage,
        },
        changeType: 'execute',
        setSchema,
      });
      setIsLoading(false);
      setHasToExecute(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleValueChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    varId: string,
  ) => {
    const updatedVariables = variables.map(variable => {
      if (variable.id === varId) {
        return { ...variable, [e.target.name]: e.target.value };
      }
      return variable;
    });

    setVariables(updatedVariables);
    setWhoIsChanging({ name: 'variables', value: updatedVariables });
    setIsChangingDirectly(true);
  };

  const handleLanguageChange = (value: string) => {
    setLanguage(value);
    setWhoIsChanging({ name: 'language', value });
    setIsChangingDirectly(true);
  };

  const handleScriptChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setScript(e.target.value);
    setWhoIsChanging({
      name: e.target.name,
      value: e.target.value,
    });
    setIsChangingDirectly(true);
  };

  const deleteVariable = (varId: string) => {
    const updatedVariables = variables.filter(
      variable => variable.id !== varId,
    );
    setVariables(updatedVariables);
    setWhoIsChanging({ name: 'variables', value: updatedVariables });
    setIsChangingDirectly(true);
  };

  const nodeType =
    nodeColorBasedOnType[type as keyof typeof nodeColorBasedOnType];
  const findColor = nodeType.colors;
  const user = useAuthStore(state => state.user);

  const handlePromptAssistantVariables = useCallback((varNames: string[]) => {
    const newVariables = varNames.slice(0, 5).map(name => ({
      id: 'func' + Math.random().toString(36).substr(2, 9),
      name,
      value: '',
    }));
    setVariables(newVariables);
    setWhoIsChanging({ name: 'variables', value: newVariables });
    setIsChangingDirectly(true);
  }, []);

  const onSubmit = async () => {
    setIsLoading(true);
    try {
      const searchParams = new URLSearchParams(window.location.search);
      const versionId = searchParams.get('versionId') || undefined;
      const response = await FlowService.executeSingleNode(
        flowId,
        id,
        undefined,
        versionId,
      );
      const execId = response?.data?.execId;
      if (!execId) {
        throw new Error('Missing execId in execute-single-node response');
      }
      setNodeState((nds: any) =>
        nds?.map((nd: any) =>
          nd.id === id
            ? { ...nd, data: { ...nd.data, currentExecId: execId } }
            : nd,
        ),
      );
    } catch (error) {
      console.error('execute-single-node failed', {
        nodeId: id,
        flowId,
        error: error instanceof Error ? error.message : String(error),
      });
      setNotification({
        type: 'error',
        message: 'Failed to run node',
      });
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const status = data?.executionStatus;
    if (status === 'COMPLETED' || status === 'FAILED') {
      setIsLoading(false);
    }
  }, [data?.executionStatus]);

  const handleRun = async (_run: string) => {
    await onSubmit();
  };

  useDebounceEffect(
    () => {
      if (isChangingDirectly) {
        setIsChangingDirectly(false);
        nodeDataAutoSaveDynamic({
          newEdges: edges,
          setNodeState,
          id,
          setSaving,
          flowId,
          objectData: { [whoIsChanging.name]: whoIsChanging.value },
          setSchema,
        });
        setWhoIsChanging({ value: '', name: '' });
      }
    },
    [isChangingDirectly, whoIsChanging],
    300,
  );

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <Box className={isLoading || btnLoading ? 'node' : ''}>
        <Node
          edges={edges}
          type={type}
          handleRun={handleRun}
          btnText="Run Script"
          onSubmit={onSubmit}
          isLoading={isLoading}
          data={data}
          id={id}
          showTokensUsed={false}
        >
          <Box display={'flex'}>
            <Box
              p={'16px'}
              borderRight={`1px solid ${
                user?.theme === 'dark' ? '#475467' : '#EAECF0'
              }`}
            >
              <ScriptEditor
                variables={variables}
                data={data}
                addVariable={addVariable}
                handleValueChange={handleValueChange}
                handleLanguageChange={handleLanguageChange}
                script={script}
                language={language}
                findColor={findColor}
                edges={edges}
                handleScriptChange={handleScriptChange}
                deleteVariable={deleteVariable}
                onPromptAssistantVariables={handlePromptAssistantVariables}
              />
            </Box>
            <Box
              p={'16px 24px 16px 16px'}
              display="flex"
              flexDirection={'column'}
            >
              <OutputTextarea
                previewIndex={data.previewIndex}
                value={data.result}
                activeHandle={
                  edges?.find(
                    (edge: any) =>
                      edge?.source === id && edge?.sourceHandle === 'output',
                  )?.source
                    ? true
                    : false
                }
                placement={data?.placement}
                labelName={'Output'}
                findColor={findColor}
                nodeLabel={data?.label}
                nodeId={id}
                inputSize={data?.outputSize}
                collapsed={data?.collapsed || false}
              />
              <OutputTextarea
                value={data.console}
                handleId="console"
                activeHandle={
                  edges?.find(
                    (edge: any) =>
                      edge?.source === id && edge?.sourceHandle === 'console',
                  )?.source
                    ? true
                    : false
                }
                placement={data?.placement}
                labelName={'Console Log'}
                findColor={findColor}
                nodeLabel={data?.label}
                nodeId={id}
                inputSize={data?.consoleSize}
                collapsed={data?.collapsed || false}
              />
            </Box>
          </Box>
        </Node>
      </Box>
    </>
  );
};

export default memo(Scripting);
